/**
 * FloatingPanelContent - PROJ-066
 * Reuses existing incident editor components in floating panel format
 * Maintains same functionality as IncidentEditorDialog
 */

import { useState, useCallback } from 'react';
import { Database, MessageSquare, Clock, Timer, Code, GitMerge, Save, X } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { IncidentDataTab } from '../IncidentEditorDialog/IncidentDataTab';
import { NotesTab } from '../IncidentEditorDialog/NotesTab';
import { PayloadTab } from '../IncidentEditorDialog/PayloadTab';
import { TimelineTab } from '../IncidentEditorDialog/TimelineTab';
import { SlaTab } from '../IncidentEditorDialog/SlaTab';
import { MergeHistoryTab } from '../IncidentEditorDialog/MergeHistoryTab';
import { useIncidentEditorForm } from '../IncidentEditorDialog/useIncidentEditorForm';
import { isIncidentReadOnly } from '@/components/incidents/KanbanView';
import type { IncidentEditorMode } from '../IncidentEditorDialog/types';
import type { Incident } from '@/types/incidents';

interface FloatingPanelContentProps {
  incident: Incident;
  onClose: () => void;
  onIncidentUpdate: () => void;
}

export function FloatingPanelContent({
  incident,
  onClose,
  onIncidentUpdate,
}: FloatingPanelContentProps) {
  const { toast } = useToast();

  // Determine initial mode - edit if not read-only, view otherwise
  const initialMode: IncidentEditorMode = isIncidentReadOnly(incident) ? 'view' : 'edit';
  const [currentMode, setCurrentMode] = useState<IncidentEditorMode>(initialMode);

  // Handle successful submit
  const handleSubmitSuccess = useCallback(async (formData: Record<string, unknown>) => {
    try {
      // The form hook handles the actual API call
      toast({
        title: 'Incidente guardado',
        description: 'Los cambios se han guardado correctamente.',
      });
      onIncidentUpdate();
    } catch (error) {
      console.error('Error saving incident:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el incidente.',
        variant: 'destructive',
      });
    }
  }, [toast, onIncidentUpdate]);

  const {
    form,
    submitError,
    setSubmitError,
    handleSubmit,
    handleSaveAndClose,
    isEditMode,
  } = useIncidentEditorForm({
    mode: currentMode,
    incident,
    open: true,
    onSubmit: handleSubmitSuccess,
    onClose,
  });

  const { handleClose: requestClose, UnsavedChangesDialog } = useUnsavedChanges({
    isDirty: currentMode !== 'view' && form.formState.isDirty,
    onClose,
  });

  const handleSwitchToEdit = useCallback(() => {
    setCurrentMode('edit');
  }, []);

  const isViewMode = currentMode === 'view';
  const canSwitchToEdit = isViewMode && !isIncidentReadOnly(incident);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <Form {...form}>
        <form
          onSubmit={(e) => {
            form.handleSubmit(handleSubmit)(e);
          }}
          className="flex flex-1 min-h-0 flex-col overflow-hidden"
        >
          {/* Tabs container */}
          <Tabs defaultValue="data" className="flex flex-col flex-1 min-h-0 overflow-hidden w-full">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border-b bg-transparent px-3 py-2 shrink-0">
              <TabsTrigger value="data" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                <Database className="h-3.5 w-3.5 opacity-60" />
                <span>Datos</span>
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                <MessageSquare className="h-3.5 w-3.5 opacity-60" />
                <span>Notas</span>
              </TabsTrigger>
              <TabsTrigger value="payload" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                <Code className="h-3.5 w-3.5 opacity-60" />
                <span>Payload</span>
              </TabsTrigger>
              <TabsTrigger value="timeline" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                <Clock className="h-3.5 w-3.5 opacity-60" />
                <span>Historial</span>
              </TabsTrigger>
              <TabsTrigger value="sla" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                <Timer className="h-3.5 w-3.5 opacity-60" />
                <span>SLA</span>
              </TabsTrigger>
              <TabsTrigger value="merge-history" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs">
                <GitMerge className="h-3.5 w-3.5 opacity-60" />
                <span>Fusiones</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab content with form fields */}
            <fieldset disabled={isViewMode} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <TabsContent value="data" className="flex flex-col flex-1 min-h-0 overflow-y-auto">
                <IncidentDataTab
                  form={form}
                  isEditMode={isEditMode}
                  incidentId={incident?.id}
                  isViewMode={isViewMode}
                  auditInfo={{
                    createdAt: incident.createdAt,
                    updatedAt: incident.updatedAt,
                    createdByName: incident.createdBy?.fullName || null,
                    updatedByName: incident.updatedBy?.fullName || null,
                  }}
                />
              </TabsContent>

              <TabsContent value="notes" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <NotesTab incidentId={incident?.id || null} />
              </TabsContent>

              <TabsContent value="payload" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <PayloadTab form={form} />
              </TabsContent>

              <TabsContent value="timeline" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <TimelineTab incidentId={incident?.id || null} />
              </TabsContent>

              <TabsContent value="sla" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <SlaTab incidentId={incident?.id || null} />
              </TabsContent>

              <TabsContent value="merge-history" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <MergeHistoryTab incidentId={incident?.id || null} />
              </TabsContent>
            </fieldset>
          </Tabs>

          {/* Footer actions */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30 shrink-0">
            <div className="flex items-center gap-2">
              {isViewMode && canSwitchToEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSwitchToEdit}
                >
                  Editar
                </Button>
              )}
              {submitError && (
                <span className="text-xs text-destructive">{submitError}</span>
              )}
            </div>

            {!isViewMode && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => requestClose()}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={form.formState.isSubmitting}
                >
                  <Save className="h-4 w-4 mr-1" />
                  {form.formState.isSubmitting ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            )}
          </div>
        </form>
      </Form>

      {UnsavedChangesDialog}
    </div>
  );
}

export default FloatingPanelContent;
